import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';

/**
 * Pixel-perfect "Print as Original Layout" view for PDF-imported forms.
 *
 * Route: /forms/:formId/print-original?submissionId=xxx
 * Fetches the stored PDF page images + field bboxes + submission values
 * and renders each page with filled values overlaid at their exact positions.
 */
export default function PrintOriginalView() {
  const { formId } = useParams();
  const [params] = useSearchParams();
  const submissionId = params.get('submissionId');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/forms/${formId}/print-original`, {
          params: submissionId ? { submission_id: submissionId } : {},
        });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load print layout');
      } finally {
        setLoading(false);
      }
    })();
  }, [formId, submissionId]);

  const handlePrint = () => window.print();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /></div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center min-h-screen gap-3 p-6">
      <p className="text-sm text-[#991B1B]">{error}</p>
      <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
    </div>;
  }
  if (!data || !data.pages?.length) {
    return <div className="text-center py-12 text-sm text-[#94A3B8]">No PDF pages available for this form.</div>;
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="print-original-view">
      {/* Print-hidden toolbar */}
      <div className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] p-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(data.submission?.patient_id ? `/patients/${data.submission.patient_id}` : '/forms'))} data-testid="print-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-sm font-semibold text-[#0A2342]">{data.name || 'Form'} — Original Layout</h1>
            {data.submission && (
              <div className="text-[11px] text-[#64748B]" data-testid="print-submission-meta">
                Filled {new Date(data.submission.submitted_at).toLocaleString()} by {data.submission.submitted_by_name || '—'}
                {data.submission.updated_at && data.submission.updated_at !== data.submission.submitted_at ? ` · Last edited ${new Date(data.submission.updated_at).toLocaleString()}` : ''}
              </div>
            )}
          </div>
        </div>
        <Button className="careiq-btn-primary flex items-center gap-2" onClick={handlePrint} data-testid="print-btn">
          <Printer className="w-4 h-4" />
          Print
        </Button>
      </div>

      {/* Pages */}
      <div className="max-w-5xl mx-auto py-8 space-y-6 print:py-0 print:space-y-0 print:max-w-none">
        {data.pages.map((pageBase64, pageIdx) => {
          const fieldsOnPage = (data.fields || []).filter(f => (f.page_index ?? 0) === pageIdx);
          return <PrintPage key={pageIdx} pageBase64={pageBase64} fields={fieldsOnPage} />;
        })}
      </div>

      {/* Print-specific styles */}
      <style>{PRINT_CSS}</style>
    </div>
  );
}

export const PRINT_CSS = `
  @media print {
    @page { margin: 0; size: auto; }
    body { background: white !important; margin: 0 !important; }
    .print-page { page-break-after: always; box-shadow: none !important; border: none !important; margin: 0 !important; }
    .print-page:last-child { page-break-after: auto; }
  }
`;

export function PrintPage({ pageBase64, fields }) {
  return (
    <div className="print-page relative bg-white shadow-md mx-auto" style={{ width: '100%', maxWidth: '850px', containerType: 'inline-size' }}>
      <div className="relative" style={{ width: '100%' }}>
        <img
          src={`data:image/png;base64,${pageBase64}`}
          alt="Form page"
          className="block w-full h-auto"
          style={{ width: '100%' }}
        />
        {/* Absolutely-positioned value overlays — positions are normalized 0-1 */}
        {fields.map(f => (
          <FieldOverlay key={f.id} field={f} />
        ))}
      </div>
    </div>
  );
}

function FieldOverlay({ field }) {
  const v = field._value;
  if (v === undefined || v === null || v === '') return null;
  const bbox = field.bbox || {};
  const style = {
    position: 'absolute',
    left: `${(bbox.x || 0) * 100}%`,
    top: `${(bbox.y || 0) * 100}%`,
    width: `${(bbox.w || 0.1) * 100}%`,
    minHeight: `${Math.max(bbox.h || 0.02, 0.02) * 100}%`,
    fontSize: '1.35cqw',
    lineHeight: '1.2',
    color: '#0A2342',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontWeight: 400,
    overflow: 'hidden',
    paddingLeft: '2px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    pointerEvents: 'none',
  };

  // Type may be stored as `field_type` (FormField model) or `type` (raw AI output)
  const fieldType = field.field_type || field.type || 'text';

  // Render checkbox as a visual "✓"
  if (fieldType === 'checkbox') {
    const checked = v === true || v === 'true' || v === 'yes' || v === 1 || v === '1';
    if (!checked) return null;
    return <div style={{ ...style, minHeight: 0, height: `${(bbox.h || 0.012) * 100}%`, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, fontSize: '1.8cqw', fontWeight: 700, lineHeight: 1 }} data-testid={`overlay-${field.id || field.field_id}`}>✓</div>;
  }

  // Signature — render as image if data URL, otherwise italic cursive text
  if (fieldType === 'signature') {
    const sv = String(v);
    if (sv.startsWith('data:image/')) {
      return (
        <img
          src={sv}
          alt="Signature"
          style={{ ...style, objectFit: 'contain', fontFamily: undefined, color: undefined }}
          data-testid={`overlay-${field.id || field.field_id}`}
        />
      );
    }
    return <div style={{ ...style, fontSize: '2cqw', fontStyle: 'italic', fontFamily: '"Segoe Script", "Brush Script MT", cursive' }} data-testid={`overlay-${field.id || field.field_id}`}>{sv}</div>;
  }

  // Multi-value (checkboxes / multi-select). Single-line values sit on the baseline, below the printed label.
  const iso = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
  const display = Array.isArray(v) ? v.join(', ') : iso ? `${v.slice(5, 7)}/${v.slice(8, 10)}/${v.slice(0, 4)}` : String(v);
  const single = ((bbox.h || 0.02) < 0.02 || fieldType !== 'textarea') && !display.includes('\n');
  // Shrink long single-line values to fit their box (≈0.55em average glyph width).
  const fit = `max(0.85cqw, min(1.35cqw, ${(((bbox.w || 0.1) * 100) / Math.max(display.length * 0.55, 1)).toFixed(3)}cqw))`;
  return <div style={single ? { ...style, display: 'flex', alignItems: 'flex-end', whiteSpace: 'nowrap', fontSize: fit, height: `${Math.max(bbox.h || 0.02, 0.012) * 100}%`, minHeight: 0 } : style} data-testid={`overlay-${field.id || field.field_id}`}>{display}</div>;
}
